/**
 * Build JSON-LD structured data for an episode detail page.
 *
 * Returns a `@graph` array of schema.org entities:
 *   - PodcastEpisode  → unlocks podcast-specific rich results in Google Search
 *   - VideoObject     → unlocks video rich results + thumbnail in Search/Discover
 *
 * Validate output at: https://search.google.com/test/rich-results
 *
 * @param {Object} episode      - cms_episodes document
 * @param {string} canonicalUrl - Absolute canonical URL of this episode page
 * @param {string} siteUrl      - Absolute origin (e.g., https://golfnutsofficial.com)
 * @param {string} youtubeId    - Extracted 11-char YouTube video ID (may be empty)
 * @param {string} description  - Plain-text (no HTML) episode description
 * @returns {Object|null} JSON-LD object ready to be stringified, or null if insufficient data
 */
export function buildEpisodeJsonLd({ episode, canonicalUrl, siteUrl, youtubeId, description }) {
  if (!episode || !canonicalUrl || !siteUrl) return null;

  const graph = [];

  // --- PodcastEpisode ---
  const podcastEpisode = {
    '@type': 'PodcastEpisode',
    name: episode.title,
    url: canonicalUrl,
    ...(description && { description }),
    ...(episode.publishDate && { datePublished: episode.publishDate }),
    ...(episode.duration && { duration: hmsToIso8601(episode.duration) }),
    partOfSeries: {
      '@type': 'PodcastSeries',
      name: 'Golf Nuts',
      url: siteUrl,
    },
  };

  // Associated audio/video URLs
  const associatedMedia = [];
  if (youtubeId) {
    associatedMedia.push({
      '@type': 'MediaObject',
      contentUrl: `https://www.youtube.com/watch?v=${youtubeId}`,
      embedUrl: `https://www.youtube.com/embed/${youtubeId}`,
    });
  }
  if (episode.spotifyUrl) {
    associatedMedia.push({ '@type': 'MediaObject', contentUrl: episode.spotifyUrl });
  }
  if (episode.applePodcastsUrl) {
    associatedMedia.push({ '@type': 'MediaObject', contentUrl: episode.applePodcastsUrl });
  }
  if (associatedMedia.length === 1) {
    podcastEpisode.associatedMedia = associatedMedia[0];
  } else if (associatedMedia.length > 1) {
    podcastEpisode.associatedMedia = associatedMedia;
  }

  // Thumbnail (prefer episode-specific, fall back to YouTube hqdefault)
  const thumbnailUrl =
    episode.thumbnail ||
    (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : null);
  if (thumbnailUrl) {
    podcastEpisode.image = thumbnailUrl;
  }

  graph.push(podcastEpisode);

  // --- VideoObject (only when there's a real YouTube video) ---
  if (youtubeId) {
    graph.push({
      '@type': 'VideoObject',
      name: episode.title,
      ...(description && { description }),
      thumbnailUrl: thumbnailUrl || `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`,
      ...(episode.publishDate && { uploadDate: episode.publishDate }),
      ...(episode.duration && { duration: hmsToIso8601(episode.duration) }),
      contentUrl: `https://www.youtube.com/watch?v=${youtubeId}`,
      embedUrl: `https://www.youtube.com/embed/${youtubeId}`,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

/**
 * Convert "HH:MM:SS" or "MM:SS" duration to ISO 8601 (`PTxHxMxS`).
 * Returns the input unchanged if it doesn't match the expected pattern,
 * so already-formatted ISO 8601 strings pass through cleanly.
 *
 * @param {string} hms
 * @returns {string}
 */
function hmsToIso8601(hms) {
  if (!hms || typeof hms !== 'string') return hms;
  // Already ISO 8601?
  if (/^PT/.test(hms.trim())) return hms.trim();
  const parts = hms.trim().split(':').map((p) => parseInt(p, 10));
  if (parts.some((n) => Number.isNaN(n))) return hms;
  let h = 0, m = 0, s = 0;
  if (parts.length === 3) [h, m, s] = parts;
  else if (parts.length === 2) [m, s] = parts;
  else return hms;
  let out = 'PT';
  if (h) out += `${h}H`;
  if (m) out += `${m}M`;
  if (s || (!h && !m)) out += `${s}S`;
  return out;
}
